import type { ReactNode } from 'react';
import { getCosts } from '@shared/helpers';
import { useUser } from '@shared/hooks';
// import { useUser } from '@shared/hooks';
import { parseAddress } from '@src/library/helpers/events/address';
import { checkEventEnd, getEventDaysHours, getShortDate } from '@src/library/helpers/events/dateSets';
// import { getDiscountEventInfo } from '@src/library/helpers/events/discount';
import { useMyTickets } from '@src/library/hooks/events';
import type { Event } from '@src/library/models/events';
import { JoinBtn } from '../join-btn';
import { LeaveBtn } from '../leave-btn';
import { Organizer } from '../organizer';
import * as st from './styles.module.scss';

interface ItemInfoProps {
  title: string;
  value?: ReactNode;
  className?: string;
  description?: string;
}

const ItemInfo: React.FC<ItemInfoProps> = ({ title, value, className, description }) => (
  <li className={st.item}>
    <span className={st.item__title} title={description ?? ''}>
      {title}
    </span>
    {value && (
      <div className={`${value ? st.item__value : st.item__empty} ${className || ''}`}>{value}</div>
    )}
  </li>
);

const ItemInfoCost = ({ title, value }: ItemInfoProps): JSX.Element => (
  <li className={st.item}>
    <span className={st.item__title}>{title}</span>
    <div className={st.item__value}>{`${value === 0 ? 'Бесплатно' : `${value} RUB`}`}</div>
  </li>
);

const EventDateTime = ({ date, timeRange }: { date: string; timeRange: string }) => {
  return (
    <div className={st.date}>
      <span>{getShortDate(date)}</span>
      <span>{timeRange}</span>
    </div>
  );
};

interface Props {
  eventInfo: Event;
  isAuthor: boolean;
  soldTickets: number;
}

export const Information = ({ eventInfo, isAuthor, soldTickets }: Props): JSX.Element => {
  //Из объекта события убрали discount
  //const promotion = getDiscountEventInfo(discount)
  const {
    mode,
    author,
    address,
    timetable,
    places,
    cost,
    language,
    refusal,
    prepayment_cost,
    costs,
    time_to,
  } = eventInfo;
  const { user } = useUser();
  const isEndEvent = checkEventEnd(time_to);

  const place = mode === 'N' ? 'Онлайн' : `${parseAddress(address)}`;
  const [days, hours] = getEventDaysHours(timetable);
  const { tickets } = useMyTickets();

  // console.log("tickets:",tickets);
  // console.log("eventInfo:",eventInfo);

  const hasCustomGroup = user?.custom_groups && user?.custom_groups?.length > 0;
  const hasDateStages = timetable.length > 1;
  const hasTickets = tickets.length > 0;

  const sortedGroupCost = [...costs]
    ?.filter((group) => user?.custom_groups?.find((custGroup) => group.group.id === custGroup.id))
    .sort((a, b) => a.cost - b.cost);

  const costValues =
    isAuthor && costs.length > 0
      ? `${getCosts(cost, costs).min / 100} - ${getCosts(cost, costs).max / 100}`
      : hasCustomGroup && sortedGroupCost.length
        ? sortedGroupCost[0].cost / 100
        : cost / 100;

  return (
    <section className={st.info}>
      <h3>Информация</h3>
      <div className={st.card}>
        <div className={st.info__head}>
          <span className={st.title}>Организатор</span>
          <Organizer author={author} />
        </div>
        <div className={st.info__body}>
          <ul className={st.list}>
            <ItemInfo title="Место встречи" value={place} />
            <ItemInfo title="Часовой пояс" value={'GMT+3 МСК'} />

            {hasDateStages ? (
              <>
                <ItemInfo title={'Дата/время'} />
                {timetable.map((timeTable, index) => (
                  <ItemInfo
                    key={index}
                    title={`Этап ${index + 1}`}
                    value={
                      <EventDateTime
                        date={timeTable.date}
                        timeRange={`${timeTable.time_from} - ${timeTable.time_to}`}
                      />
                    }
                  />
                ))}
              </>
            ) : (
              <ItemInfo
                title={'Дата/время'}
                value={
                  <EventDateTime
                    date={timetable[0].date}
                    timeRange={`${timetable[0].time_from} - ${timetable[0].time_to}`}
                  />
                }
              />
            )}

            {days > 1 && <ItemInfo title="Кол-во дней" value={days} />}

            <ItemInfo
              title="Кол-во часов"
              value={hours}
              description="Суммарное колличество часов"
            />
            {refusal > 0 && (
              <ItemInfo
                title="Отменить"
                value={refusal}
                description="Время за которое вы можеье отказаться от участия в мероприятии с возвратом оплаченной суммы"
              />
            )}
            {language && <ItemInfo title="Язык" value={language} />}
            <ItemInfo title="Осталось мест" value={places - soldTickets} />
            <ItemInfoCost title="Стоимость" value={costValues} />
            {prepayment_cost > 0 && (
              <ItemInfoCost title="Предоплата" value={prepayment_cost / 100} />
            )}
            {/*
                Из объекта события убрали discount
                {promotion && <ItemPromotion title='Акция' value={`${promotion.discountCost} RUB до ${promotion.discountTo}`} />} */}
          </ul>
          {!hasTickets && !isAuthor && (
            <div className={st.info__action}>
              <JoinBtn />
            </div>
          )}
          {hasTickets && !isEndEvent && (
            <div className={st.info__action}>
              <LeaveBtn />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
